import type { ApplePayPaymentRequestMethod } from '@orders'
import type { IRogue } from '../services/RogueTypes'
import { messages } from './messages'
import { getMessageContext } from './helpers'

export const APPLEPAY_SUPPORT_APP_VERSION = '3.1.0'

let paymentRequestInstance: ApplePayPaymentRequestMethod
let $rogue: IRogue

const partialApplePayAddressToRogue = ({
  region,
  city,
  country,
  postalCode,
}) => ({
  region    : region || '',
  city      : city || '',
  postcode  : postalCode || '',
  countryId : country || '',
  // apple pay doesn't send following fields on rates request
  email     : '',
  street    : [''],
  firstname : '',
  lastname  : '',
  telephone : '',
})

const normalizeApplePayAddress = ({ emailAddress, postalAddress, name, phoneNumber }) => ({
  emailAddress,
  phoneNumber,
  administrativeArea : postalAddress.state || postalAddress.administrativeArea,
  locality           : postalAddress.city,
  postalCode         : postalAddress.postalCode,
  addressLines       : postalAddress.street?.split(/\r?\n/),
  givenName          : name.givenName,
  familyName         : name.familyName,
  countryCode        : postalAddress.ISOCountryCode,
})

const onAppMessage = async ({ data }) => {
  try {
    data = typeof data === 'string' ? JSON.parse(data) : data
  } catch (e) {
    return
  }

  if (!(data && (data.app || data.mobileApp))) {
    return
  }

  if (data.type === messages.APPLEPAY_SHIPPING_ADDRESS) {
    // address is changed on the app side in apple pay
    paymentRequestInstance.order.updateShippingState({ status: 'loading' })
    await paymentRequestInstance.order.updateShippingAddress(partialApplePayAddressToRogue(data.data))
    // send updated details to the app
    $rogue.emitToApp(messages.APPLEPAY_UPDATE_DETAILS, {
      ...paymentRequestInstance.getDetails(),
      shippingOptions: paymentRequestInstance.getShippingMethods(),
    })
  }
  if (data.type === messages.APPLEPAY_SHIPPING_METHOD) {
    // shipping method is changed  on the app side in apple pay
    await paymentRequestInstance.order.updateShippingMethod([data.data])
    // send updated details to the app
    $rogue.emitToApp(messages.APPLEPAY_UPDATE_DETAILS, paymentRequestInstance.getDetails())
  }
  if (data.type === messages.APPLEPAY_SUCCESS) {
    // apple pay payment success on the app side
    const orderData = {
      shippingContact : normalizeApplePayAddress(data.data.shippingContact),
      billingContact  : normalizeApplePayAddress(data.data.billingContact || data.data.shippingContact),
      token           : {
        paymentData           : data.data.paymentData,
        paymentMethod         : data.data.paymentMethod,
        transactionIdentifier : data.data.transactionIdentifier,
      },
    }

    await paymentRequestInstance.updateOrder(orderData)
    paymentRequestInstance.order
      .place()
      .then(() => {
        // send success to the app
        $rogue.emitToApp(messages.APPLEPAY_STATUS_SUCCESS)
        paymentRequestInstance.request = undefined
      })
      .catch(() => {
        // send failure to the app
        $rogue.emitToApp(messages.APPLEPAY_STATUS_FAILURE)
        paymentRequestInstance.request = undefined
      })
  }
  if (data.type === messages.APPLEPAY_CANCEL) {
    // cancel apple pay payment on the app side
    paymentRequestInstance.order.setPaymentState(false)
    paymentRequestInstance.request = undefined
  }
}

export const init = (rogue: IRogue, instance: ApplePayPaymentRequestMethod) => {
  paymentRequestInstance = instance
  $rogue = rogue

  if (paymentRequestInstance.request) {
    paymentRequestInstance.request.removeEventListener('message', onAppMessage)
    paymentRequestInstance.request = undefined
  }
  paymentRequestInstance.order.setPaymentState(true)

  paymentRequestInstance.request = getMessageContext(rogue.appContext!)
  paymentRequestInstance.request.addEventListener('message', onAppMessage)

  $rogue.emitToApp(
    messages.APPLEPAY_START,
    {
      details         : paymentRequestInstance.getDetails(),
      requestShipping : !paymentRequestInstance.virtual,
    }
  )
}
